// Element-type classification Sets + difficulty / element-grouping taxonomy,
// extracted verbatim from InteractiveGraph.js (IG-split Slice 4, pure-function
// seam). Pure data tables + one pure classifier (matchElementTypeToGroupKey).
//
// NOTE: the label / description / hint strings below are hardcoded Japanese,
// carried over UNCHANGED from the inline definitions. This is pre-existing i18n
// debt (these have not been migrated to ig.* translation keys); Slice 4 only
// relocates them byte-identically and does NOT attempt to fix that debt.

export const BAND_ELEMENT_TYPES = new Set(['band_segment', 'band', 'band_area', 'area_segment']);
export const DOT_ELEMENT_TYPES = new Set(['dot', 'dot_plot', 'dot_series', 'dot_item']);
export const VENN_CIRCLE_ELEMENT_TYPES = new Set(['set_circle', 'venn_circle', 'circle']);
export const VENN_REGION_ELEMENT_TYPES = new Set(['venn_region', 'intersection', 'set_intersection']);
export const TREE_NODE_ELEMENT_TYPES = new Set(['tree_node', 'node']);
export const TREE_BRANCH_ELEMENT_TYPES = new Set(['tree_branch', 'branch', 'edge']);

export const DIFFICULTY_TYPE_INFO = {
  attention_allocation: {
    label: '注意配分',
    badgeClass: 'rounded-full',
    badgeStyle: { background: 'var(--accent-bg)', color: 'var(--accent)' },
    description: '視線を一度に複数へ向けづらい生徒向けに、要素を限定して順に提示しています。',
    supportText: '明るい要素のみ集中エリアです。不要な項目は非表示にして視線を休ませましょう。',
    transitionHint: '次のステージでは新しい要素が少数ずつ追加されます。色の強調を切り替えて集中範囲を調整してください。',
    completionHint: 'すべてのフォーカス段階を見終えました。必要に応じて再び任意のステージに戻れます。'
  },
  information_integration: {
    label: '情報統合',
    badgeClass: 'rounded-full',
    badgeStyle: { background: 'var(--primary-bg)', color: 'var(--primary)' },
    description: '前段の情報を保持しながら少しずつ要素を積み上げ、全体像を合成できるように構成しています。',
    supportText: '各ステージでは「前ステージ＋新要素」の関係を明示しているので、差分を意識して確認しましょう。',
    transitionHint: '次のステージでは関連データが追加されます。比較コメントや凡例を併読して繋がりを確認してください。',
    completionHint: '全ステージを統合し終えました。差分確認のために一時的にハイライトを戻すこともできます。'
  },
  default: {
    label: '学習ステージ',
    badgeClass: 'rounded-full',
    badgeStyle: { background: 'var(--bg-muted)', color: 'var(--text-muted)' },
    description: 'ステージごとに可視要素と説明が整理されています。',
    supportText: '現在のステージに合わせて可視要素を切り替えると、情報量をコントロールできます。',
    transitionHint: '次のステージに進むと別の要素グループが追加されます。',
    completionHint: '全ステージを確認しました。'
  }
};

export const DIFFICULTY_GROUP_HINTS = {
  attention_allocation: {
    trend: '線は一本ずつ追って視線を固定し、不要な線は非表示にして集中を保ちましょう。',
    comparison: '棒や箱は色数が多いため、重要な項目のみオンにして縦横の比較を絞りましょう。',
    composition: '割合のステージは一つずつ強調されます。色の強弱を頼りに割合を確認してください。',
    markers: '散布点は注目エリアだけ彩度を上げています。重なりが気になる場合は要素を一旦隠せます。',
    other: '凡例や注釈は補助情報です。必要なときのみ一時的に表示しましょう。'
  },
  information_integration: {
    trend: '線グループごとに前ステージとのつながりを説明します。重ね合わせの順番に注目してください。',
    comparison: '棒や箱は区間を追加しながら全体像を作ります。数直線の位置関係を確認しましょう。',
    composition: '割合データは部分→全体の順で表示されます。合計値との整合に意識を向けてください。',
    markers: 'ポイントは領域ごとに出現します。コメント欄でまとめ方をメモすると統合しやすくなります。',
    other: '凡例や注記は各ステージで更新されます。統合視点で読み直すと差分が整理できます。'
  }
};

export const DEFAULT_GROUP_HINTS = {
  trend: '時間変化や傾向を追う際に使います。',
  comparison: '量の比較や分布を確かめる要素です。',
  composition: '全体に対する割合を確認できます。',
  markers: '個別のポイントや目印です。',
  other: '凡例や注釈など補助情報です。'
};

export const ELEMENT_GROUP_DEFINITIONS = [
  {
    key: 'trend',
    label: '傾向 / 変化',
    description: '折れ線や面グラフなど時間的な変化を示す要素です。',
    matchers: ['line', 'area'],
    accent: '📈'
  },
  {
    key: 'comparison',
    label: '比較 / 分布',
    description: '棒・ヒストグラム・箱など量の比較に使う要素です。',
    matchers: ['bar', 'histogram', 'box'],
    accent: '📊'
  },
  {
    key: 'composition',
    label: '割合 / 構成',
    description: '円グラフやセグメントなど全体に対する割合を示す要素です。',
    matchers: ['pie', 'segment', 'slice'],
    accent: '🧩'
  },
  {
    key: 'markers',
    label: 'ポイント / ハイライト',
    description: '散布点や個別マーカーなど単体で位置を示す要素です。',
    matchers: ['scatter', 'point', 'dot', 'data_point'],
    accent: '🔎'
  },
  {
    key: 'other',
    label: '補助要素',
    description: '凡例や注釈など、前景以外の情報を示す要素です。',
    matchers: [],
    accent: '🧭'
  }
];

export const matchElementTypeToGroupKey = (elementType) => {
  const normalized = (elementType || '').toLowerCase();
  if (!normalized) {
    return 'other';
  }
  const matchedDefinition = ELEMENT_GROUP_DEFINITIONS.find((definition) =>
    (definition.matchers || []).some((matcher) => normalized.includes(matcher))
  );
  return matchedDefinition?.key || 'other';
};
